<template>
  <div v-if="!showContactForm">
    <div class="popover-heading-row flex pb-4">
      <p
        class="font-bold mb-0"
        data-i18n="memberTravelCompanionsPopupTitle"
      >
        {{ $t('memberTravelCompanionsPopupTitle') }}
      </p>
      <fa
        class="cursor-pointer"
        icon="close"
        size="xl"
        @click="closePopover"
      />
    </div>
    <ul class="mb-0 pt-2">
      <li
        v-for="(person, index) in registeredPersonsList"
        :key="index"
        class="popover-singlefinder-item flex flex-col"
      >
        <div class="popover-singlefinder-row flex pb-4">
          <div class="flex popover-singlefinder-info">
            <div class="user-icon-bg flex justify-center items-center">
              <fa
                icon="user"
                style="color: #fff"
              />
            </div>
            <p class="mb-0 px-2">
              {{ maleOrFemale(person.sex) }},
            </p>
            <p class="mb-0">
              {{ person.age }} år
            </p>
          </div>
          <div
            v-if="person.id !== profile.id"
            class="flex cursor-pointer"
            @click="contactPerson(person)"
          >
            <p class="mb-0 pr-2 text-blue font-bold">
              Kontakta
            </p>
          </div>
          <div
            v-if="person.id === profile.id"
            class="flex"
          >
            <p class="mb-0 pr-2 text-turquoise font-bold">
              Du
            </p>
          </div>
        </div>
      </li>
    </ul>
  </div>
  <div v-else-if="showContactForm">
    <div class="popover-heading-row flex pb-4">
      <p
        class="font-bold mb-0"
        data-i18n="memberTravelCompanionsContactPopupTitle"
      >
        {{ $t('memberTravelCompanionsContactPopupTitle') }}
      </p>
      <fa
        class="cursor-pointer"
        icon="close"
        size="xl"
        @click="closePopover"
      />
    </div>
    <div
      v-if="!formSuccess"
      class="popover-contact-form pt-2"
    >
      <div class="popover-singlefinder-item flex flex-col">
        <div class="popover-singlefinder-row flex pb-6">
          <div class="flex popover-singlefinder-info">
            <div class="user-icon-bg flex justify-center items-center">
              <fa
                class="text-white"
                icon="user"
              />
            </div>
            <p class="mb-0 px-2">
              {{ maleOrFemale(personToContact.sex) }},
            </p>
            <p class="mb-0">
              {{ personToContact.age }} år
            </p>
          </div>
        </div>
      </div>
      <div class="popover-contact-form flex flex-col items-start mb-6">
        <p
          class="font-bold mb-2"
          data-i18n="memberTravelCompanionsContactEmailLabel"
        >
          {{ $t('memberTravelCompanionsContactEmailLabel') }}
        </p>
        <input
          v-model="emailPhone"
          class="form-input"
          type="text"
          disabled
          :placeholder="$t('memberTravelCompanionsContactEmailLabel')"
        />
      </div>
      <div class="popover-contact-form flex flex-col items-start">
        <p
          class="font-bold mb-2"
          data-i18n="bookInfoCustomerNotifications"
        >
          {{ $t('bookInfoCustomerNotifications') }}
        </p>
        <textarea
          v-model="personalMessage"
          class="form-textarea"
          type="text"
          data-i18n="memberTravelCompanionsContactTextareaPH"
          :placeholder="$t('memberTravelCompanionsContactTextareaPH')"
        />
      </div>
      <div class="popover-contact-send flex justify-end pt-4">
        <button
          class="btn btn-primary rounded-[1.25rem] px-4"
          data-i18n="competitionSubmit"
          @click="sendForm"
        >
          {{ $t('competitionSubmit') }}
        </button>
      </div>
    </div>
    <div
      v-else-if="formSuccess"
      class="popover-contact-form-success"
    >
      <div class="popover-singlefinder-item flex flex-col">
        <div class="popover-singlefinder-row flex pb-6 w-full">
          <div class="flex popover-singlefinder-info justify-center align-item-center pt-4">
            <p>
              {{ $t('memberFormSent') }}
            </p>
          </div>
        </div>
        <div class="popover-contact-send flex justify-end">
          <button
            class="btn btn-primary rounded-[1.25rem] px-4"
            @click="closePopover"
          >
            {{ $t('close') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="error">
    <p>
      {{ $t('error.bookedservice.structured.failed') }}
    </p>
  </div>
</template>

<script setup>
const { t } = useI18n()
const userStore = useUserStore()

defineProps({
  registeredPersonsList: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['close', 'hide-arrow'])
const showContactForm = ref(false)
const personToContact = ref(null)
const emailPhone = ref(userStore.user?.email)
const personalMessage = ref('')
const formSuccess = ref(false)
const error = ref(false)
const profile = ref(userStore.memberProfile?.config)

const closePopover = () => {
  formSuccess.value = false
  error.value = false
  emit('close')
}

const contactPerson = (person) => {
  emit('hide-arrow')
  personToContact.value = person
  showContactForm.value = true
}

const maleOrFemale = (sex) => {
  return sex === 'M'
    ? t('formGenderM')
    : t('formGenderF')
}

const sendForm = async () => {
  const payload = {
    available_id: personToContact.value.available_id,
    message: personalMessage.value,
  }

  const result = await userStore.memberSingleFinderContact(payload)

  if (!result) {
    return error.value = true
  }
  formSuccess.value = true
}
</script>

<style lang="scss" scoped>
.popover {
  &-heading-row {
    justify-content: space-between;
    border-bottom: 1px solid rgba(0, 0, 0, 0.14);
  }

  &-singlefinder-row {
    justify-content: space-between;
    align-items: center;
  }

  &-singlefinder-info {
    align-items: center;

    .user-icon-bg {
      background-color: theme('colors.teal');
      border-radius: 50%;
      padding: 5px;
      width: 34px;
      height: 34px;
    }
  }

  &-contact-form {
    .form-input, .form-textarea {
      width: 100%;
      border:none;
      background-color: theme('colors.medium-light-gray');
      border-radius: 16px;
      padding: 10px;
    }
    .form-textarea {
      height: 240px;
    }
  }
}
</style>